/**
 * Image Orientation Correction Utility
 *
 * Handles EXIF orientation for mobile photos to prevent upside-down/rotated images.
 * Mobile devices often save photos with EXIF metadata indicating rotation,
 * but this metadata is lost during server processing, causing display issues.
 *
 * This utility reads EXIF orientation and physically rotates the image data
 * before upload, ensuring images are stored in the correct orientation.
 */

/**
 * EXIF orientation values:
 * 1 = Normal (0°)
 * 2 = Flip horizontal
 * 3 = Rotate 180°
 * 4 = Flip vertical
 * 5 = Rotate 90° CW + Flip horizontal
 * 6 = Rotate 90° CW
 * 7 = Rotate 90° CCW + Flip horizontal
 * 8 = Rotate 90° CCW
 */

/**
 * Reads EXIF orientation from image file
 */
const getExifOrientation = (file: File): Promise<number> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const view = new DataView(e.target?.result as ArrayBuffer);

      // Check for valid JPEG file
      if (view.getUint16(0, false) !== 0xFFD8) {
        resolve(1); // Not a JPEG, no orientation needed
        return;
      }

      const length = view.byteLength;
      let offset = 2;

      while (offset < length) {
        // Check if we've reached the end of the file
        if (offset + 2 > length) {
          resolve(1);
          return;
        }

        const marker = view.getUint16(offset, false);
        offset += 2;

        // Check for APP1 marker (0xFFE1) which contains EXIF data
        if (marker === 0xFFE1) {
          if (offset + 2 > length) {
            resolve(1);
            return;
          }

          offset += 2;

          // Check for "Exif" header
          if (offset + 6 > length) {
            resolve(1);
            return;
          }

          if (view.getUint32(offset, false) !== 0x45786966) {
            resolve(1);
            return;
          }

          offset += 6; // Skip "Exif\0\0"

          // Read byte order
          const tiffOffset = offset;
          const byteOrder = view.getUint16(tiffOffset, false);
          const littleEndian = byteOrder === 0x4949;

          offset += view.getUint32(tiffOffset + 4, littleEndian);

          // Read number of IFD entries
          const tags = view.getUint16(tiffOffset + offset, littleEndian);
          offset += 2;

          // Search for orientation tag (0x0112)
          for (let i = 0; i < tags; i++) {
            const entryOffset = tiffOffset + offset + (i * 12);

            if (entryOffset + 12 > length) {
              resolve(1);
              return;
            }

            const tag = view.getUint16(entryOffset, littleEndian);

            if (tag === 0x0112) { // Orientation tag
              const orientation = view.getUint16(entryOffset + 8, littleEndian);
              resolve(orientation);
              return;
            }
          }

          resolve(1); // No orientation tag found
          return;
        } else if ((marker & 0xFF00) !== 0xFF00) {
          // Not a valid marker
          break;
        } else {
          // Skip other markers
          if (offset + 2 > length) {
            resolve(1);
            return;
          }
          offset += view.getUint16(offset, false);
        }
      }

      resolve(1); // Default orientation
    };

    reader.onerror = () => resolve(1);
    reader.readAsArrayBuffer(file.slice(0, 64 * 1024)); // Read first 64KB (EXIF is usually in first few KB)
  });
};

/**
 * Corrects image orientation based on EXIF data
 * Returns a new File object with corrected orientation
 */
export const correctImageOrientation = async (file: File): Promise<File> => {
  // Only process image files
  if (!file.type.startsWith('image/')) {
    return file;
  }

  // Get EXIF orientation
  const orientation = await getExifOrientation(file);

  // If orientation is normal (1), return original file
  if (orientation === 1) {
    return file;
  }

  // Load image
  const image = await loadImage(file);

  // Create canvas with correct dimensions
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    console.error('Failed to get canvas context');
    return file;
  }

  // Set canvas dimensions based on orientation
  // Orientations 5-8 swap width and height
  if (orientation >= 5 && orientation <= 8) {
    canvas.width = image.height;
    canvas.height = image.width;
  } else {
    canvas.width = image.width;
    canvas.height = image.height;
  }

  // Apply transformations based on orientation
  switch (orientation) {
    case 2:
      // Flip horizontal
      ctx.transform(-1, 0, 0, 1, canvas.width, 0);
      break;
    case 3:
      // Rotate 180°
      ctx.transform(-1, 0, 0, -1, canvas.width, canvas.height);
      break;
    case 4:
      // Flip vertical
      ctx.transform(1, 0, 0, -1, 0, canvas.height);
      break;
    case 5:
      // Rotate 90° CW + Flip horizontal
      ctx.transform(0, 1, 1, 0, 0, 0);
      break;
    case 6:
      // Rotate 90° CW
      ctx.transform(0, 1, -1, 0, canvas.width, 0);
      break;
    case 7:
      // Rotate 90° CCW + Flip horizontal
      ctx.transform(0, -1, -1, 0, canvas.width, canvas.height);
      break;
    case 8:
      // Rotate 90° CCW
      ctx.transform(0, -1, 1, 0, 0, canvas.height);
      break;
  }

  // Draw image with transformations
  ctx.drawImage(image, 0, 0);

  // Convert canvas to Blob
  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob from canvas'));
        }
      },
      file.type,
      0.95 // Quality for JPEG (0.95 = high quality)
    );
  });

  // Create new File from Blob with original filename and metadata
  return new File([blob], file.name, {
    type: file.type,
    lastModified: Date.now(),
  });
};

/**
 * Helper function to load image from File
 */
const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(url); // Clean up object URL
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    image.src = url;
  });
};

/**
 * Batch process multiple files for orientation correction
 * Useful for bulk uploads
 */
export const correctMultipleImages = async (
  files: File[],
  onProgress?: (processed: number, total: number) => void
): Promise<File[]> => {
  const correctedFiles: File[] = [];

  for (let i = 0; i < files.length; i++) {
    try {
      const correctedFile = await correctImageOrientation(files[i]);
      correctedFiles.push(correctedFile);

      if (onProgress) {
        onProgress(i + 1, files.length);
      }
    } catch (error) {
      console.error(`Failed to correct orientation for ${files[i].name}:`, error);
      // On error, use original file
      correctedFiles.push(files[i]);
    }
  }

  return correctedFiles;
};
